/**
 * Generation Orchestration Service for GenFlappy
 *
 * Manages sequential asset generation with cache-first strategy, API integration,
 * and demo asset fallback. Implements Story 4.3 requirements.
 *
 * @module services/orchestrationService
 */

import type { AssetType } from '../types/validation';
import type { CropMetadata } from '../types/cache';
import { getAsset, saveAsset } from './cacheService';
import { loadDemoAsset, activateDemoMode } from './demoAssets';
import type { CustomAssets } from './demoAssets';
import { logGenerationPerformance, type SessionMetrics } from '../utils/performanceLogger';
import {
  detectAPIFailure,
  detectAssetLoadFailure,
  detectTimeoutFailure,
  detectNetworkFailure,
  updateDemoModeState,
  logError,
} from './errorDetectionService';
import type { DemoModeState } from '../types/errors';
import { validateAsset } from './qualityValidation';

/**
 * Convert image to true grayscale
 *
 * AI models cannot reliably generate perfect grayscale images - they add subtle
 * color tints that fail validation. This function forces true grayscale by
 * converting each pixel to its luminance value (R=G=B).
 *
 * Uses standard grayscale formula: Gray = 0.299*R + 0.587*G + 0.114*B
 *
 * @param {string} imageDataUrl - Base64 data URL of the image
 * @returns {Promise<string>} Grayscale image as base64 data URL
 */
export async function convertToGrayscale(imageDataUrl: string): Promise<string> {
  return new Promise((resolve, reject) => {
    const img = new Image();

    img.onload = () => {
      try {
        const canvas = document.createElement('canvas');
        canvas.width = img.naturalWidth || img.width;
        canvas.height = img.naturalHeight || img.height;

        const ctx = canvas.getContext('2d');
        if (!ctx) {
          throw new Error('Failed to get canvas 2D context for grayscale conversion');
        }

        // Draw original image
        ctx.drawImage(img, 0, 0);

        // Get pixel data
        const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
        const data = imageData.data;

        // Convert to grayscale using luminance formula
        for (let i = 0; i < data.length; i += 4) {
          const r = data[i];
          const g = data[i + 1];
          const b = data[i + 2];
          // Alpha (data[i+3]) is preserved

          // Standard grayscale formula (maintains proper brightness)
          const gray = Math.round(0.299 * r + 0.587 * g + 0.114 * b);

          data[i] = gray;     // R
          data[i + 1] = gray; // G
          data[i + 2] = gray; // B
          // data[i + 3] = alpha (unchanged)
        }

        // Put modified pixel data back
        ctx.putImageData(imageData, 0, 0);

        // Convert to data URL
        const grayscaleDataUrl = canvas.toDataURL('image/png');
        resolve(grayscaleDataUrl);
      } catch (error) {
        const errorMsg = error instanceof Error ? error.message : 'Unknown error';
        console.error('Grayscale conversion failed:', errorMsg);
        reject(new Error(`Grayscale conversion failed: ${errorMsg}`));
      }
    };

    img.onerror = () => {
      reject(new Error('Failed to load image for grayscale conversion'));
    };

    img.crossOrigin = 'anonymous';
    img.src = imageDataUrl;
  });
}

/**
 * Resize image to exact dimensions using canvas
 *
 * Takes a base64 data URL and resizes it to the exact target dimensions
 * for the asset type. Uses canvas to perform high-quality resizing.
 * Logs detailed dimension information for debugging validation failures.
 *
 * Exported for use by ErrorBoundary and other components that need to
 * resize demo assets before loading them into the game.
 *
 * @param {string} imageDataUrl - Base64 data URL of the image
 * @param {AssetType} assetType - Type of asset (determines target dimensions)
 * @returns {Promise<string>} Resized image as base64 data URL
 *
 * @example
 * ```typescript
 * const resized = await resizeImageToTarget('data:image/png;base64,...', 'character');
 * // Returns 128x128px image
 * ```
 */
export async function resizeImageToTarget(
  imageDataUrl: string,
  assetType: AssetType
): Promise<string> {
  return new Promise((resolve, reject) => {
    const img = new Image();

    img.onload = () => {
      try {
        // Get original dimensions
        const originalWidth = img.naturalWidth || img.width;
        const originalHeight = img.naturalHeight || img.height;

        // Get target dimensions for this asset type
        const target = {
          character: { width: 128, height: 128 },
          obstacle: { width: 256, height: 512 },
          background: { width: 720, height: 1280 },
        }[assetType];

        console.log(`Resizing ${assetType}:`, {
          original: `${originalWidth}x${originalHeight}`,
          target: `${target.width}x${target.height}`,
        });

        // Create canvas with exact target dimensions
        const canvas = document.createElement('canvas');
        canvas.width = target.width;
        canvas.height = target.height;

        const ctx = canvas.getContext('2d');
        if (!ctx) {
          throw new Error('Failed to get canvas 2D context for resizing');
        }

        // Draw image scaled to exact dimensions
        // Use high-quality image smoothing
        ctx.imageSmoothingEnabled = true;
        ctx.imageSmoothingQuality = 'high';
        ctx.drawImage(img, 0, 0, target.width, target.height);

        // Convert canvas to data URL
        const resizedDataUrl = canvas.toDataURL('image/png');

        // Verify the resize was successful by checking output size
        if (!resizedDataUrl || resizedDataUrl.length < 100) {
          throw new Error('Resize produced invalid data URL');
        }

        console.log(`Resize successful for ${assetType}: ${target.width}x${target.height}`);
        resolve(resizedDataUrl);
      } catch (error) {
        const errorMsg = error instanceof Error ? error.message : 'Unknown error';
        console.error(`Resize failed for ${assetType}:`, errorMsg);
        reject(new Error(`Image resize failed for ${assetType}: ${errorMsg}`));
      }
    };

    img.onerror = (event) => {
      console.error(`Image load failed for resizing ${assetType}:`, event);
      reject(new Error(`Failed to load image for resizing ${assetType}`));
    };

    img.crossOrigin = 'anonymous';
    img.src = imageDataUrl;
  });
}

/**
 * Orchestration state tracking
 *
 * @enum {string}
 */
export enum OrchestrationState {
  IDLE = 'IDLE',
  GENERATING_CHARACTER = 'GENERATING_CHARACTER',
  GENERATING_OBSTACLE = 'GENERATING_OBSTACLE',
  GENERATING_BACKGROUND = 'GENERATING_BACKGROUND',
  COMPLETE = 'COMPLETE',
  PARTIAL_DEMO = 'PARTIAL_DEMO',
  FULL_DEMO = 'FULL_DEMO',
}

/**
 * Source of a generated asset
 *
 * @typedef {'custom' | 'demo' | 'cached'} AssetSource
 */
export type AssetSource = 'custom' | 'demo' | 'cached';

/**
 * Result of orchestration process
 *
 * @interface OrchestrationResult
 * @property {Record<AssetType, string>} assets - Generated assets as base64 data URLs
 * @property {Record<AssetType, AssetSource>} assetSources - Source of each asset
 * @property {Record<AssetType, CropMetadata | undefined>} assetMetadata - Metadata for collision detection
 * @property {OrchestrationState} state - Final orchestration state
 * @property {number} totalDuration - Total generation time in milliseconds
 */
export interface OrchestrationResult {
  /** Generated assets as base64 data URLs */
  assets: Record<AssetType, string>;

  /** Source of each asset (custom/demo/cached) */
  assetSources: Record<AssetType, AssetSource>;

  /** Metadata for pixel-perfect collision detection */
  assetMetadata: Record<AssetType, CropMetadata | undefined>;

  /** Final orchestration state */
  state: OrchestrationState;

  /** Total generation time in milliseconds */
  totalDuration: number;
}

/**
 * Progress callback for orchestration updates
 *
 * @callback OnProgressCallback
 * @param {AssetType} currentAsset - Asset currently being generated
 * @param {number} progress - Progress percentage (0-100)
 * @param {number} elapsedTime - Elapsed time in milliseconds
 * @param {Object} partialFailures - Partial failure information (Story 6.2)
 * @param {AssetType[]} partialFailures.failedAssets - Assets that fell back to demo mode
 * @param {string[]} partialFailures.messages - User-friendly failure messages
 */
export type OnProgressCallback = (
  currentAsset: AssetType,
  progress: number,
  elapsedTime: number,
  partialFailures?: {
    failedAssets: AssetType[];
    messages: string[];
  }
) => void;

/**
 * Client-side timeout duration (45 seconds) - Architecture line 88
 */
const CLIENT_TIMEOUT_MS = 45000;

/**
 * Cache hit display delay (1 second) - AC2
 */
const CACHE_HIT_DELAY_MS = 1000;

/**
 * Generate a single asset with cache-first strategy
 *
 * Implements the following flow:
 * 1. Check IndexedDB cache via getAsset() (fuzzy matching automatic)
 * 2. If cache hit: return cached asset, skip API
 * 3. If cache miss: call API with 45s timeout
 * 4. If API success: cache via saveAsset() and return
 * 5. If API/timeout/validation fails: fallback to demo asset
 *
 * @param {string} prompt - User prompt for the asset
 * @param {AssetType} assetType - Type of asset to generate
 * @param {IDBDatabase | null} db - IndexedDB database instance (null = API-only mode)
 * @param {AbortSignal} [signal] - Abort signal for request cancellation
 * @returns {Promise<{imageData: string; source: AssetSource}>} Generated asset and its source
 *
 * @example
 * ```typescript
 * const result = await generateAsset('dragon', 'character', db, signal);
 * console.log(`Got ${result.source} asset: ${result.imageData.substring(0, 50)}...`);
 * ```
 */
export async function generateAsset(
  prompt: string,
  assetType: AssetType,
  db: IDBDatabase | null,
  signal?: AbortSignal
): Promise<{ imageData: string; source: AssetSource; cropMetadata?: CropMetadata; failureReason?: string }> {
  const startTime = performance.now();

  try {
    // Step 1: Cache check (AC2)
    if (db) {
      const cachedAsset = await getAsset(db, prompt, assetType);

      if (cachedAsset) {
        console.log(`Loading cached ${assetType}... ✓`);

        // AC2: Wait 1000ms before advancing to next asset
        await new Promise((resolve) => setTimeout(resolve, CACHE_HIT_DELAY_MS));

        // Story 4.5: Log performance metrics (cache hit)
        const endTime = performance.now();
        logGenerationPerformance({
          assetType,
          startTime,
          endTime,
          duration: endTime - startTime,
          cacheHit: true,
        });

        return {
          imageData: cachedAsset.imageData,
          source: 'cached',
          cropMetadata: cachedAsset.cropMetadata,
        };
      }
    }

    // Step 2: API call with client-side timeout (AC3, Architecture line 105)
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), CLIENT_TIMEOUT_MS);

    // Combine external signal with timeout signal
    if (signal) {
      signal.addEventListener('abort', () => controller.abort());
    }

    try {
      // Story 4.5: Track API latency
      const apiStartTime = performance.now();
      const response = await fetch(`/api/generate-${assetType}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ prompt }),
        signal: controller.signal,
      });
      const apiLatency = performance.now() - apiStartTime;

      clearTimeout(timeoutId);

      if (!response.ok) {
        throw new Error(`API returned ${response.status}: ${response.statusText}`);
      }

      const data = await response.json();

      // AC3: Validate API response
      if (!data?.imageData || typeof data.imageData !== 'string') {
        throw new Error('Invalid API response');
      }

      // Extract crop metadata for collision detection
      const cropMetadata = data.cropMetadata as CropMetadata | undefined;

      // Step 2.3: Convert to true grayscale (AI models add color tints that fail validation)
      let processedImageData: string;
      try {
        processedImageData = await convertToGrayscale(data.imageData);
        console.log(`Converted ${assetType} to true grayscale`);
      } catch (grayscaleError) {
        console.warn(`Failed to convert ${assetType} to grayscale, using original:`, grayscaleError);
        // Continue with original - may fail grayscale validation but worth trying
        processedImageData = data.imageData;
      }

      // Step 2.5: Resize image to exact dimensions before validation/caching
      // This is CRITICAL - dimension validation requires exact sizes
      let finalImageData: string;
      try {
        finalImageData = await resizeImageToTarget(processedImageData, assetType);
      } catch (resizeError) {
        console.error(`CRITICAL: Failed to resize ${assetType}:`, resizeError);
        // If resize fails, the asset will likely fail dimension validation
        // Continue with original but expect quality validation to fail
        finalImageData = processedImageData;
        console.warn(`Using original image - expect dimension validation failure for ${assetType}`);
      }

      // Story 6.3 Task 3: Quality validation before caching (automatic demo substitution)
      const validationResult = await validateAsset(finalImageData, assetType);

      // Log all validation results for debugging
      console.log(`Quality validation for ${assetType}:`, {
        passed: validationResult.passed,
        scores: {
          overall: validationResult.overallScore,
          grayscale: validationResult.grayscaleScore,
          dimension: validationResult.dimensionScore,
          transparency: validationResult.transparencyScore,
        },
        threshold: 80,
        failureReasons: validationResult.failureReasons,
      });

      if (!validationResult.passed) {
        console.warn(`Asset failed quality validation, substituting with demo ${assetType}:`, {
          failureReasons: validationResult.failureReasons,
          scores: {
            overall: validationResult.overallScore,
            grayscale: validationResult.grayscaleScore,
            dimension: validationResult.dimensionScore,
            transparency: validationResult.transparencyScore,
          },
          threshold: 80,
        });

        // Log quality failure for tracking
        logError({
          errorType: 'quality',
          message: `Quality validation failed for ${assetType}: ${validationResult.failureReasons.join(', ')}`,
          timestamp: Date.now(),
          severity: 'warning',
          demoModeActivated: 'partial',
        });

        // Load demo asset as automatic substitution
        let demoImageData = await loadDemoAsset(assetType);

        // CRITICAL: Convert to grayscale and resize demo asset
        // Demo assets need to be:
        // 1. Converted to true grayscale (may have color tints)
        // 2. Resized from 30x30, 52x480 to 128x128, 256x512 to pass assetLoader validation
        try {
          // Step 1: Convert to grayscale
          demoImageData = await convertToGrayscale(demoImageData);
          console.log(`Converted demo ${assetType} to true grayscale`);

          // Step 2: Resize to target dimensions
          demoImageData = await resizeImageToTarget(demoImageData, assetType);
          console.log(`Resized demo ${assetType} to target dimensions`);
        } catch (processError) {
          console.error(`CRITICAL: Failed to process demo ${assetType}:`, processError);
          // If demo processing fails, the asset will fail assetLoader validation
          // This is a critical failure - the demo fallback won't work
          console.warn(`Demo ${assetType} will likely fail game asset loading validation`);
        }

        // Story 4.5: Log performance metrics (quality failure → demo)
        const endTime = performance.now();
        logGenerationPerformance({
          assetType,
          startTime,
          endTime,
          duration: endTime - startTime,
          cacheHit: false,
          apiLatency,
        });

        // Story 6.3 Code Review Fix: Return failureReason for user-facing messages
        return {
          imageData: demoImageData,
          source: 'demo',
          cropMetadata: undefined,
          failureReason: 'quality',
        };
      }

      // Step 3: Cache successful generation (already validated above)
      if (db) {
        try {
          await saveAsset(db, prompt, assetType, finalImageData, cropMetadata);
        } catch (cacheError) {
          // Non-blocking - log but continue
          console.warn('Failed to cache asset:', cacheError);
        }
      }

      // Story 4.5: Log performance metrics (API success)
      const endTime = performance.now();
      logGenerationPerformance({
        assetType,
        startTime,
        endTime,
        duration: endTime - startTime,
        cacheHit: false,
        apiLatency,
      });

      return {
        imageData: finalImageData,
        source: 'custom',
        cropMetadata,
      };
    } catch (apiError) {
      clearTimeout(timeoutId);

      // Story 6.1: Detect and log error with errorDetectionService
      const errorDuration = performance.now() - startTime;
      const detectionStart = performance.now();

      // Detect error type and create error context (priority order)
      let errorContext = null;
      if (apiError instanceof Error) {
        // 1. Check for timeout first (most specific)
        errorContext = detectTimeoutFailure(errorDuration);

        // 2. Check for network failure (offline, fetch errors)
        if (!errorContext) {
          errorContext = detectNetworkFailure(apiError);
        }

        // 3. Check for API-specific errors (rate limits, server errors)
        if (!errorContext) {
          errorContext = detectAPIFailure(apiError);
        }

        // 4. Fallback to generic asset load failure
        if (!errorContext) {
          errorContext = detectAssetLoadFailure(apiError, assetType);
        }
      }

      // Measure detection overhead
      const detectionOverhead = performance.now() - detectionStart;
      if (process.env.NODE_ENV === 'development' && detectionOverhead > 10) {
        console.warn(`Error detection took ${detectionOverhead.toFixed(2)}ms (threshold: 10ms)`);
      }

      // Log error with full context
      if (errorContext) {
        logError({
          ...errorContext,
          demoModeActivated: 'pending', // Will be determined after fallback
        });
      }

      // AC3: Structured error logging (Story 4.5: preserved for backwards compatibility)
      console.error('Asset generation failed', {
        prompt,
        assetType,
        errorType: apiError instanceof Error ? apiError.message : 'Unknown error',
        timestamp: Date.now(),
        duration: errorDuration,
        durationFormatted: `${(errorDuration / 1000).toFixed(1)}s`,
      });

      throw apiError; // Re-throw to trigger demo fallback
    }
  } catch {
    // Step 4: Demo fallback (AC4)
    console.log(`Falling back to demo asset for ${assetType}`);

    try {
      const demoImageData = await loadDemoAsset(assetType);

      // AC4: Handle 1x1 transparent fallback gracefully
      if (demoImageData.length < 200) {
        console.warn(`Using transparent fallback for ${assetType}`);
      }

      // Story 4.5: Log performance metrics (demo fallback)
      const endTime = performance.now();
      logGenerationPerformance({
        assetType,
        startTime,
        endTime,
        duration: endTime - startTime,
        cacheHit: false,
      });

      return {
        imageData: demoImageData,
        source: 'demo',
        cropMetadata: undefined, // Demo assets don't have metadata
      };
    } catch (demoError) {
      console.error('Demo asset fallback failed:', demoError);

      // Story 4.5: Log performance metrics (ultimate fallback)
      const endTime = performance.now();
      logGenerationPerformance({
        assetType,
        startTime,
        endTime,
        duration: endTime - startTime,
        cacheHit: false,
      });

      // Ultimate fallback: 1x1 transparent PNG
      return {
        imageData:
          'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAYAAAAfFcSJAAAADUlEQVR42mNk+M9QDwADhgGAWjR9awAAAABJRU5ErkJggg==',
        source: 'demo',
        cropMetadata: undefined, // Ultimate fallback doesn't have metadata
      };
    }
  }
}

/**
 * Trigger automatic demo mode fallback (Story 6.3 Tasks 1, 2, 4)
 *
 * Automatically activates full demo mode when errors are detected:
 * - Timeout exceeded (45s)
 * - Network offline
 * - API errors (500, 503, etc.)
 * - Quality validation failures
 *
 * @param {string} reason - Trigger reason ('timeout' | 'offline' | 'api_error' | 'quality')
 * @param {OnProgressCallback} onProgress - Progress callback to update UI
 * @param {AbortController} [abortController] - Optional AbortController to cancel pending requests
 * @returns {Promise<OrchestrationResult>} Complete demo assets result
 *
 * @example
 * ```typescript
 * const result = await triggerAutomaticDemoMode('timeout', onProgress, controller);
 * // Returns full demo assets with FULL_DEMO state
 * ```
 */
async function triggerAutomaticDemoMode(
  reason: 'timeout' | 'offline' | 'api_error' | 'quality',
  onProgress: OnProgressCallback,
  abortController?: AbortController
): Promise<OrchestrationResult> {
  const start = performance.now();

  // 1. Cancel pending API calls (if any)
  if (abortController) {
    abortController.abort();
  }

  // 2. Log automatic demo mode activation (Story 6.3 Code Review Fix: Use actual reason)
  console.log(`Automatic demo mode: ${reason}`, {
    reason,
    timestamp: Date.now(),
  });

  // 3. Load all demo assets via activateDemoMode() from Story 6.2
  const demoAssets: CustomAssets = await activateDemoMode();

  // 3.5. CRITICAL: Convert to grayscale and resize all demo assets
  // Demo assets need to be:
  // 1. Converted to true grayscale (may have color tints)
  // 2. Resized from 30x30, 52x480 to 128x128, 256x512 to pass assetLoader validation
  console.log('Converting demo assets to grayscale and resizing for full demo mode...');
  try {
    // Step 1: Convert to grayscale
    const [grayCharacter, grayObstacle, grayBackground] = await Promise.all([
      convertToGrayscale(demoAssets.character),
      convertToGrayscale(demoAssets.obstacle),
      convertToGrayscale(demoAssets.background),
    ]);

    // Step 2: Resize to target dimensions
    const [resizedCharacter, resizedObstacle, resizedBackground] = await Promise.all([
      resizeImageToTarget(grayCharacter, 'character'),
      resizeImageToTarget(grayObstacle, 'obstacle'),
      resizeImageToTarget(grayBackground, 'background'),
    ]);

    demoAssets.character = resizedCharacter;
    demoAssets.obstacle = resizedObstacle;
    demoAssets.background = resizedBackground;

    console.log('All demo assets converted to grayscale and resized successfully');
  } catch (error) {
    console.error('CRITICAL: Failed to process demo assets:', error);
    // If demo assets can't be processed, they will fail game asset loading validation
    // This is a critical failure but we continue anyway (user will see error in game)
  }

  // 4. Log error context for tracking (Sentry placeholder)
  logError({
    errorType: reason,
    timestamp: Date.now(),
    message: `Automatic demo mode activated: ${reason}`,
    severity: 'warning',
    demoModeActivated: 'full',
  });

  // 5. Update UI state via progress callback (Story 6.2 integration)
  const toastMessage = getToastMessage(reason);
  onProgress('background', 100, performance.now() - start, {
    failedAssets: ['character', 'obstacle', 'background'],
    messages: [toastMessage],
  });

  // 6. Return demo assets as OrchestrationResult
  const totalDuration = performance.now() - start;

  // Story 6.3 Code Review Fix: Performance measurement and validation (<2s requirement)
  console.log('Automatic demo mode activation time:', totalDuration.toFixed(1), 'ms');

  if (totalDuration > 2000) {
    console.warn(
      `⚠️ Demo mode activation exceeded 2s target: ${totalDuration.toFixed(1)}ms (AC requirement: <2s)`
    );
  }

  return {
    assets: {
      character: demoAssets.character,
      obstacle: demoAssets.obstacle,
      background: demoAssets.background,
    },
    assetSources: {
      character: 'demo',
      obstacle: 'demo',
      background: 'demo',
    },
    assetMetadata: {
      character: undefined,
      obstacle: undefined,
      background: undefined,
    },
    state: OrchestrationState.FULL_DEMO,
    totalDuration,
  };
}

/**
 * Get user-friendly toast message for automatic demo mode activation
 *
 * @param {string} reason - Activation reason
 * @returns {string} User-friendly toast message
 */
function getToastMessage(reason: string): string {
  switch (reason) {
    case 'timeout':
      return 'Loaded demo mode';
    case 'offline':
      return 'Offline mode - playing demo';
    case 'api_error':
      return 'Loaded demo mode';
    case 'quality':
      return 'Using demo assets for best experience';
    default:
      return 'Loaded demo mode';
  }
}

/**
 * Orchestrate sequential generation of all three assets
 *
 * Executes character → obstacle → background in sequence (NO parallelization).
 * Calls onProgress callback after each asset completes with updated state.
 *
 * Final state determination:
 * - COMPLETE: All assets custom or cached
 * - PARTIAL_DEMO: Some assets failed → demo
 * - FULL_DEMO: All assets failed → demo
 *
 * @param {Record<AssetType, string>} prompts - Prompts for each asset type
 * @param {IDBDatabase | null} db - IndexedDB database instance
 * @param {OnProgressCallback} onProgress - Progress update callback
 * @param {AbortSignal} [signal] - Abort signal for request cancellation
 * @returns {Promise<OrchestrationResult>} Complete orchestration result
 *
 * @example
 * ```typescript
 * const result = await orchestrateGeneration(
 *   { character: 'dragon', obstacle: 'volcano', background: 'sky' },
 *   db,
 *   (asset, progress, elapsed) => console.log(`${asset}: ${progress}%`),
 *   signal
 * );
 * ```
 */
export async function orchestrateGeneration(
  prompts: Record<AssetType, string>,
  db: IDBDatabase | null,
  onProgress: OnProgressCallback,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  _signal?: AbortSignal
): Promise<OrchestrationResult> {
  const overallStartTime = performance.now();

  const assets: Record<AssetType, string> = {} as Record<AssetType, string>;
  const assetSources: Record<AssetType, AssetSource> = {} as Record<AssetType, AssetSource>;
  const assetMetadata: Record<AssetType, CropMetadata | undefined> = {} as Record<AssetType, CropMetadata | undefined>;

  const assetTypes: AssetType[] = ['character', 'obstacle', 'background'];
  const progressSteps = [33, 66, 100];

  // Story 4.5: Initialize session metrics (clear on new generation start)
  const sessionMetrics: SessionMetrics = {
    totalGenerationTime: 0,
    assetCount: 0,
    cacheHits: 0,
    averageGenerationTime: 0,
    sessionStart: overallStartTime,
  };

  // Story 6.1: Initialize demo mode state tracking
  let demoModeState: DemoModeState = {
    failedAssets: [],
    mode: 'none',
    timestamp: Date.now(),
  };

  // Story 6.2: Helper to generate user-friendly failure messages
  // Story 6.3 Code Review Fix: Support multiple failure reasons (quality, timeout, etc.)
  const failureReasons: Record<AssetType, string> = {} as Record<AssetType, string>;

  const generateFailureMessages = (failedAssets: AssetType[]): string[] => {
    return failedAssets.map((asset) => {
      const reason = failureReasons[asset] || 'timeout';

      switch (reason) {
        case 'quality':
          return `Using demo ${asset} - quality validation failed`;
        case 'timeout':
        default:
          return `Using demo ${asset} - generation timed out`;
      }
    });
  };

  // Story 6.3 Task 1: Create AbortController for cancelling API requests on timeout
  const abortController = new AbortController();

  // Story 6.3 Task 2: Check if browser is offline before making any API requests
  if (!navigator.onLine) {
    console.log('Offline detected, triggering automatic demo mode');
    return await triggerAutomaticDemoMode('offline', onProgress, abortController);
  }

  // AC1: Sequential generation
  for (let i = 0; i < assetTypes.length; i++) {
    const assetType = assetTypes[i];
    const prompt = prompts[assetType];

    // Update progress: starting asset generation
    const elapsedTime = performance.now() - overallStartTime;

    // Story 6.3 Task 1: Check for timeout before each asset (45s threshold)
    if (elapsedTime > CLIENT_TIMEOUT_MS) {
      console.log('Timeout detected, triggering automatic demo mode', {
        elapsedMs: elapsedTime,
        threshold: CLIENT_TIMEOUT_MS,
      });
      return await triggerAutomaticDemoMode('timeout', onProgress, abortController);
    }

    const partialFailures = demoModeState.failedAssets.length > 0
      ? {
          failedAssets: demoModeState.failedAssets,
          messages: generateFailureMessages(demoModeState.failedAssets),
        }
      : undefined;
    onProgress(assetType, i === 0 ? 0 : progressSteps[i - 1], elapsedTime, partialFailures);

    // Story 6.3 Task 4: Declare result outside try-catch for proper scoping
    let result;

    // Story 6.3 Task 4: Wrap in try-catch to detect API errors and trigger full demo mode
    try {
      // Generate asset (Story 6.3: Use abortController signal for timeout cancellation)
      result = await generateAsset(prompt, assetType, db, abortController.signal);

      assets[assetType] = result.imageData;
      assetSources[assetType] = result.source;
      assetMetadata[assetType] = result.cropMetadata;

      // Story 6.3 Code Review Fix: Store failure reason for user-friendly messages
      if (result.failureReason) {
        failureReasons[assetType] = result.failureReason;
      }
    } catch (error) {
      // Check if this is an API error (not timeout, not network offline)
      if (error instanceof Error) {
        const apiError = detectAPIFailure(error);

        // API errors (500, 503, 429) trigger full automatic demo mode
        if (apiError) {
          console.log('API error detected, triggering automatic demo mode:', apiError.message);
          return await triggerAutomaticDemoMode('api_error', onProgress, abortController);
        }
      }

      // For non-API errors, re-throw to let generateAsset's demo fallback handle it
      throw error;
    }

    // Story 6.1: Update demo mode state tracking when asset uses demo fallback
    if (result.source === 'demo') {
      demoModeState = updateDemoModeState(demoModeState, assetType, true);
      console.log('Partial demo mode activated:', {
        mode: demoModeState.mode,
        failedAssets: demoModeState.failedAssets,
        failureReason: result.failureReason || 'unknown',
        assetSources: { ...assetSources },
      });
    }

    // Story 4.5: Update session metrics after each asset
    sessionMetrics.assetCount++;
    if (result.source === 'cached') {
      sessionMetrics.cacheHits++;
    }

    // Update progress: asset completed
    const completedElapsed = performance.now() - overallStartTime;
    const completedPartialFailures = demoModeState.failedAssets.length > 0
      ? {
          failedAssets: demoModeState.failedAssets,
          messages: generateFailureMessages(demoModeState.failedAssets),
        }
      : undefined;
    onProgress(assetType, progressSteps[i], completedElapsed, completedPartialFailures);
  }

  // Calculate total duration
  const totalDuration = performance.now() - overallStartTime;

  // AC5: Determine final state
  const sourceValues = Object.values(assetSources);
  const demoCount = sourceValues.filter((s) => s === 'demo').length;

  let state: OrchestrationState;
  if (demoCount === 0) {
    state = OrchestrationState.COMPLETE;
  } else if (demoCount === 3) {
    state = OrchestrationState.FULL_DEMO;
  } else {
    state = OrchestrationState.PARTIAL_DEMO;
  }

  // Story 4.5: Finalize session metrics and save to sessionStorage
  sessionMetrics.totalGenerationTime = totalDuration;
  sessionMetrics.averageGenerationTime =
    sessionMetrics.assetCount > 0 ? totalDuration / sessionMetrics.assetCount : 0;

  try {
    sessionStorage.setItem('genflappy_session_metrics', JSON.stringify(sessionMetrics));
  } catch (storageError) {
    // Non-blocking - log but continue if sessionStorage unavailable
    console.warn('Failed to save session metrics to sessionStorage:', storageError);
  }

  return {
    assets,
    assetSources,
    assetMetadata,
    state,
    totalDuration,
  };
}
